/**
 * Authentication helper functions
 * SECURITY: No direct Supabase database queries - all operations go through backend API
 */

import { supabase } from "@/integrations/supabase/client";
import { apiClient } from "@/shared/services/api";

/**
 * Upsert user profile from Supabase Auth metadata
 * SECURITY: Uses backend API instead of direct database access
 */
export async function upsertUserProfileFromAuth() {
  // Get user from Supabase Auth (Auth SDK is OK to use)
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const m = user.user_metadata ?? {};
  let first_name = m.first_name || m.given_name || null;
  let last_name = m.last_name || m.family_name || null;
  let full_name: string | null = null;

  if (first_name && last_name) {
    full_name = `${first_name} ${last_name}`;
  } else if (m.full_name) {
    full_name = m.full_name;
    const parts = full_name.split(' ');
    if (parts.length >= 2) {
      first_name = parts[0];
      last_name = parts.slice(1).join(' ');
    }
  } else if (m.name) {
    full_name = m.name;
    const parts = full_name.split(' ');
    if (parts.length >= 2) {
      first_name = parts[0];
      last_name = parts.slice(1).join(' ');
    }
  }

  // Use backend API instead of direct database query
  try {
    await apiClient.put('/user/profile', {
      first_name,
      last_name,
      name: full_name,
      email: user.email,
      phone: m.phone,
      avatar_url: m.avatar_url ?? m.picture,
    });
  } catch (error) {
    console.error('[authHelpers] Failed to upsert profile via backend:', error);
    throw error;
  }
}

/**
 * Load user data (profile, customer, role, subscriber)
 * SECURITY: Uses backend API GET /api/user/me instead of 4 separate Supabase queries
 *
 * @deprecated Use AuthContext's loadUserData instead - this function is kept for backwards compatibility
 */
export async function loadUserData(userId: string) {
  console.warn('[authHelpers] loadUserData is deprecated - use AuthContext instead');

  try {
    // Use single backend API call instead of 4 separate Supabase queries
    const response = await apiClient.get<{
      ok: boolean;
      user: any;
      company: any;
      userRole: any;
      subscription: any;
    }>('/user/me');

    if (!response.ok) {
      throw new Error('Failed to load user data from backend');
    }

    // Transform to match old format for backwards compatibility
    return {
      profile: response.user,
      customer: response.company,
      role: response.userRole,
      subscriber: response.subscription,
    };
  } catch (error) {
    console.error('[authHelpers] Failed to load user data from backend:', error);
    throw error;
  }
}
